import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Landmark,
  Plus,
  Info,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Copy,
  Building2,
  Hash,
  User as UserIcon,
  Loader2,
  Pencil,
  Lock,
  Trash2,
  ShieldCheck,
  Check,
} from "lucide-react";

import { cn } from "../../lib/utils";
import type { LenderBankAccount } from "../../types/schema";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { getLocalStorageItem } from "../../utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const bankAccountSchema = z
  .object({
    accountType: z.enum(["current", "virtual"]),
    accountHolderName: z.string().trim().min(2, "Account holder name is required").max(120),
    bankName: z.string().trim().min(2, "Bank name is required").max(120),
    branchName: z.string().trim().min(2, "Branch name is required").max(120),
    accountNumber: z
      .string()
      .trim()
      .min(9, "Account number must be at least 9 digits")
      .max(18, "Account number can be at most 18 digits")
      .regex(/^[0-9A-Z]+$/i, "Only letters and numbers allowed"),
    confirmAccountNumber: z.string().trim().min(1, "Please re-enter the account number"),
    ifscCode: z
      .string()
      .trim()
      .transform((v) => v.toUpperCase())
      .refine((v) => ifscRegex.test(v), "Invalid IFSC code (e.g. HDFC0001234)"),
  })
  .refine((d) => d.accountNumber === d.confirmAccountNumber, {
    message: "Account numbers do not match",
    path: ["confirmAccountNumber"],
  });

const initialBankAccountForm = {
  accountType: "current" as "current" | "virtual",
  accountHolderName: "",
  bankName: "",
  branchName: "",
  accountNumber: "",
  confirmAccountNumber: "",
  ifscCode: "",
};

const maskAccount = (n: string) => {
  if (!n) return "";
  if (n.length <= 4) return `••${n}`;
  return `••••${n.slice(-4)}`;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "object" && error !== null) {
    const payload = error as Record<string, unknown>;
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
    const nested = payload.data;
    if (typeof nested === "object" && nested !== null) {
      const nestedMessage = (nested as Record<string, unknown>).message;
      if (typeof nestedMessage === "string" && nestedMessage.trim()) {
        return nestedMessage;
      }
    }
  }
  return fallback;
};

export const formatBankForCopy = (a: LenderBankAccount, lenderName?: string) => {
  const lines = [
    lenderName ? `Lender: ${lenderName}` : null,
    `Account Holder Name: ${a.accountHolderName}`,
    `Account Type: ${a.accountType === "current" ? "Current Account" : "Virtual Account"}`,
    `Bank Name: ${a.bankName}`,
    `Branch Name: ${a.branchName}`,
    `Account Number: ${a.accountNumber}`,
    `IFSC Code: ${a.ifscCode}`,
  ].filter(Boolean);
  return lines.join("\n");
};

type VerifyData = {
  account_status?: string;
  name_at_bank?: string;
  bank_name?: string;
  branch_name?: string;
  utr?: string;
  account_number?: string;
  account_holder_name?: string;
  ifsc_code?: string;
};

const isTruthyFlag = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return false;
};

const normalizeBankAccount = (raw: Record<string, unknown>): LenderBankAccount => {
  const accountTypeRaw = String(raw.accountType ?? raw.account_type ?? "current").toLowerCase();
  return {
    id: String(raw.id ?? raw.bank_account_id ?? ""),
    lender: String(raw.lender ?? raw.lender_name ?? ""),
    customerId: raw.customerId ? String(raw.customerId) : raw.customer_id ? String(raw.customer_id) : undefined,
    accountType: accountTypeRaw === "virtual" ? "virtual" : "current",
    accountHolderName: String(raw.accountHolderName ?? raw.account_holder_name ?? ""),
    bankName: String(raw.bankName ?? raw.bank_name ?? ""),
    branchName: String(raw.branchName ?? raw.branch_name ?? ""),
    accountNumber: String(raw.accountNumber ?? raw.account_number ?? ""),
    ifscCode: String(raw.ifscCode ?? raw.ifsc_code ?? ""),
    createdAt: String(raw.createdAt ?? raw.created_at ?? new Date().toISOString()),
    createdBy: raw.createdBy ? String(raw.createdBy) : raw.created_by ? String(raw.created_by) : undefined,
    isVerified: isTruthyFlag(
      raw.isVerified ?? raw.is_verified ?? raw.is_verifed
    ),
    nameAtBank: raw.nameAtBank
      ? String(raw.nameAtBank)
      : raw.name_at_bank
        ? String(raw.name_at_bank)
        : undefined,
    utr: raw.utr ? String(raw.utr) : undefined,
    accountStatus: raw.accountStatus
      ? String(raw.accountStatus)
      : raw.account_status
        ? String(raw.account_status)
        : undefined,
    bankVerifiedAt: raw.bankVerifiedAt
      ? String(raw.bankVerifiedAt)
      : raw.bank_verified_at
        ? String(raw.bank_verified_at)
        : null,
  };
};

interface LenderBankAccountsSheetProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selectedLenderId?: string | number;
  lender?: string;
  /** When true, opens the add form by default. */
  defaultAddOpen?: boolean;
  /** When provided, shows radio selector and calls back with the selected account. */
  selectable?: boolean;
  selectedAccountId?: string;
  onSelect?: (account: LenderBankAccount) => void;
}

export default function LenderBankAccountsSheet({
  open,
  onOpenChange,
  selectedLenderId,
  lender,
  defaultAddOpen = false,
  selectable = false,
  selectedAccountId,
  onSelect,
}: LenderBankAccountsSheetProps) {
  const [accounts, setAccounts] = useState<LenderBankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAccounts = useCallback(async () => {
    if (!selectedLenderId) return;
    setLoading(true);
    try {
      const response = await apiService.get<{ data?: unknown } | unknown>(
        `/lender/get-lender-bank-accounts/${selectedLenderId}`
      );
      const root = response as Record<string, unknown>;
      const raw = root?.data ?? response;
      const list: unknown[] = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { accounts?: unknown[] })?.accounts)
          ? (raw as { accounts: unknown[] }).accounts
          : [];
      setAccounts(list.map((item) => normalizeBankAccount(item as Record<string, unknown>)));
    } catch (error) {
      console.error("Failed to fetch lender bank accounts:", error);
      toast.error("Failed to load bank accounts");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLenderId]);

  const [showAddForm, setShowAddForm] = useState(defaultAddOpen);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<LenderBankAccount | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(initialBankAccountForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [verified, setVerified] = useState(false);
  const [verifyData, setVerifyData] = useState<VerifyData | null>(null);
  const [verifiedAt, setVerifiedAt] = useState("");
  const [validating, setValidating] = useState(false);

  const accountsMatch =
    form.accountNumber.length > 0 &&
    form.confirmAccountNumber.length > 0 &&
    form.accountNumber === form.confirmAccountNumber;
  const accountMismatch =
    form.accountNumber.length > 0 &&
    form.confirmAccountNumber.length > 0 &&
    form.accountNumber !== form.confirmAccountNumber;
  const canValidate =
    !!form.accountHolderName.trim() &&
    accountsMatch &&
    form.accountNumber.length >= 9 &&
    ifscRegex.test(form.ifscCode) &&
    !validating &&
    !verified;

  const clearVerification = () => {
    setVerified(false);
    setVerifyData(null);
    setVerifiedAt("");
  };

  const resetForm = useCallback(() => {
    setForm(initialBankAccountForm);
    setErrors({});
    setEditingAccountId(null);
    setVerified(false);
    setVerifyData(null);
    setVerifiedAt("");
    setValidating(false);
  }, []);

  const buildBankAccountPayload = (
    parsed: z.infer<typeof bankAccountSchema>,
    opts?: {
      isVerified?: boolean;
      verifyData?: VerifyData | null;
      bankVerifiedAt?: string;
    }
  ) => {
    const userDetails = getLocalStorageItem("detail");
    const userId = userDetails?.userId;
    const verifiedFlag = opts?.isVerified === true;
    const vd = opts?.verifyData;

    return {
      selected_lender_id: Number(selectedLenderId),
      account_type: parsed.accountType,
      account_holder_name:
        (verifiedFlag && vd?.account_holder_name) || parsed.accountHolderName,
      bank_name: (verifiedFlag && vd?.bank_name) || parsed.bankName,
      branch_name: (verifiedFlag && vd?.branch_name) || parsed.branchName,
      account_number: (verifiedFlag && vd?.account_number) || parsed.accountNumber,
      ifsc_code: (verifiedFlag && vd?.ifsc_code) || parsed.ifscCode,
      // Backend column: lender_bank_details.is_verified
      is_verified: verifiedFlag,
      ...(verifiedFlag
        ? {
            name_at_bank: vd?.name_at_bank || "",
            utr: vd?.utr || "",
            account_status: vd?.account_status || "Valid",
            bank_verified_at:
              opts?.bankVerifiedAt || verifiedAt || new Date().toISOString(),
          }
        : {}),
      ...(userId !== undefined && userId !== null ? { userId } : {}),
    };
  };

  const buildUpdatePayload = (
    parsed: z.infer<typeof bankAccountSchema>,
    bankAccountId: string,
    opts?: {
      isVerified?: boolean;
      verifyData?: VerifyData | null;
      bankVerifiedAt?: string;
    }
  ) => ({
    bank_account_id: String(bankAccountId),
    ...buildBankAccountPayload(parsed, opts),
  });

  /** Persist create/update. Returns saved account or null on validation/API failure. */
  const persistBankAccount = async (opts: {
    isVerified: boolean;
    verifyData?: VerifyData | null;
    formOverride?: typeof form;
    bankVerifiedAt?: string;
    successMessage?: string;
  }): Promise<LenderBankAccount | null> => {
    if (!selectedLenderId) return null;

    const baseForm = opts.formOverride ?? form;
    const vd = opts.verifyData;
    const formForParse =
      opts.isVerified && vd
        ? {
            ...baseForm,
            accountHolderName: vd.account_holder_name || baseForm.accountHolderName,
            accountNumber: vd.account_number || baseForm.accountNumber,
            confirmAccountNumber: vd.account_number || baseForm.confirmAccountNumber,
            ifscCode: vd.ifsc_code || baseForm.ifscCode,
            bankName: vd.bank_name || baseForm.bankName || "Bank",
            branchName: vd.branch_name || baseForm.branchName || "Branch",
          }
        : baseForm;

    const parsed = bankAccountSchema.safeParse(formForParse);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.issues.forEach((i) => {
        errs[i.path[0] as string] = i.message;
      });
      setErrors(errs);
      toast.error("Please fix the highlighted fields before saving");
      return null;
    }

    setSaving(true);
    try {
      const saveOpts = {
        isVerified: opts.isVerified,
        verifyData: vd ?? null,
        bankVerifiedAt: opts.bankVerifiedAt,
      };
      const response = editingAccountId
        ? await apiService.put<{ data?: Record<string, unknown> } | Record<string, unknown>>(
            "/lender/update-lender-bank-account",
            buildUpdatePayload(parsed.data, editingAccountId, saveOpts)
          )
        : await apiService.post<{ data?: Record<string, unknown> } | Record<string, unknown>>(
            "/lender/create-lender-bank-account",
            buildBankAccountPayload(parsed.data, saveOpts)
          );

      const root = response as Record<string, unknown>;
      const savedRaw = (root?.data ?? response) as Record<string, unknown>;
      const saved = normalizeBankAccount({
        ...savedRaw,
        is_verified: opts.isVerified || savedRaw.is_verified,
      });

      toast.success(
        opts.successMessage ||
          (editingAccountId
            ? `Bank account updated • ${saved.bankName} • ${maskAccount(saved.accountNumber)}${
                opts.isVerified ? " • Verified" : ""
              }`
            : `Bank account added • ${saved.bankName} • ${maskAccount(saved.accountNumber)}${
                opts.isVerified ? " • Verified" : ""
              }`)
      );

      resetForm();
      setShowAddForm(false);
      await fetchAccounts();
      if (selectable && onSelect) onSelect(saved);
      return saved;
    } catch (error) {
      console.error(
        editingAccountId
          ? "Failed to update lender bank account:"
          : "Failed to create lender bank account:",
        error
      );
      toast.error(
        getApiErrorMessage(
          error,
          editingAccountId ? "Failed to update bank account" : "Failed to save bank account"
        )
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open && selectedLenderId) {
      void fetchAccounts();
    }
  }, [open, selectedLenderId, fetchAccounts]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  useEffect(() => {
    if (open && !loading && !editingAccountId) {
      setShowAddForm(defaultAddOpen || accounts.length === 0);
    }
  }, [open, defaultAddOpen, accounts.length, loading, editingAccountId]);

  const handleStartEdit = (account: LenderBankAccount) => {
    setEditingAccountId(account.id);
    setForm({
      accountType: account.accountType,
      accountHolderName: account.accountHolderName,
      bankName: account.bankName,
      branchName: account.branchName,
      accountNumber: account.accountNumber,
      confirmAccountNumber: account.accountNumber,
      ifscCode: account.ifscCode,
    });
    setErrors({});
    clearVerification();
    setShowAddForm(true);
  };

  const handleValidate = async () => {
    if (!selectedLenderId) return;
    if (!canValidate) {
      if (accountMismatch) toast.error("Account numbers do not match");
      else toast.error("Please fill account holder, account number, and IFSC correctly");
      return;
    }
    setValidating(true);
    try {
      const response: any = await apiService.post("/lender/verify-lender-bank", {
        selected_lender_id: Number(selectedLenderId) || selectedLenderId,
        lender_id: Number(selectedLenderId) || selectedLenderId,
        custName: form.accountHolderName.trim(),
        custAcctNo: form.accountNumber,
        custIfsc: form.ifscCode,
      });
      const data = (response?.data ?? response) as VerifyData;
      const verifiedAtDisplay = new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const bankVerifiedAtIso = new Date().toISOString();

      const nextForm = {
        ...form,
        accountHolderName: data.account_holder_name || form.accountHolderName,
        accountNumber: data.account_number || form.accountNumber,
        confirmAccountNumber: data.account_number || form.confirmAccountNumber,
        ifscCode: data.ifsc_code || form.ifscCode,
        bankName: data.bank_name || form.bankName,
        branchName: data.branch_name || form.branchName,
      };

      setVerifyData(data);
      setVerified(true);
      setVerifiedAt(verifiedAtDisplay);
      setForm(nextForm);

      // On successful verify, persist to DB with is_verified = true
      const saved = await persistBankAccount({
        isVerified: true,
        verifyData: data,
        formOverride: nextForm,
        bankVerifiedAt: bankVerifiedAtIso,
        successMessage:
          response?.message ||
          `Verified & saved • ${nextForm.bankName || "Bank"} • ${maskAccount(nextForm.accountNumber)}`,
      });

      if (!saved) {
        toast.info("Verified, but save failed — fix fields and use Save Account");
      }
    } catch (err: unknown) {
      clearVerification();
      toast.error(getApiErrorMessage(err, "Bank verification failed"));
    } finally {
      setValidating(false);
    }
  };

  const handleCancelForm = () => {
    resetForm();
    setShowAddForm(accounts.length === 0);
  };

  const handleDelete = async () => {
    if (!accountToDelete?.id) return;
    setDeleting(true);
    try {
      await apiService.delete(`/lender/delete-lender-bank-account/${accountToDelete.id}`, {
        selected_lender_id: selectedLenderId,
      });
      toast.success("Bank account deleted successfully");
      if (editingAccountId === accountToDelete.id) {
        resetForm();
        setShowAddForm(accounts.length <= 1);
      }
      setAccountToDelete(null);
      await fetchAccounts();
    } catch (error) {
      console.error("Failed to delete lender bank account:", error);
      toast.error(getApiErrorMessage(error, "Failed to delete bank account"));
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    // Save without verification allowed; verified saves set is_verified = true.
    await persistBankAccount({
      isVerified: verified && !!verifyData,
      verifyData,
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-primary" />
            Lender Bank Accounts
          </SheetTitle>
          <SheetDescription>
            {lender ? (
              <>For <span className="font-medium text-foreground">{lender}</span></>
            ) : (
              "Bank accounts are managed per lender."
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {!selectedLenderId && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Select a lender to manage bank accounts.</span>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading bank accounts...
            </div>
          )}

          {/* Saved Accounts */}
          {selectedLenderId && !loading && accounts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Saved Accounts ({accounts.length})
                </Label>
              </div>

              {selectable ? (
                <RadioGroup
                  value={selectedAccountId || ""}
                  onValueChange={(v) => {
                    const a = accounts.find((x:any) => x.id === v);
                    if (a && onSelect) onSelect(a);
                  }}
                  className="space-y-2"
                >
                  {accounts.map((a:any) => (
                    <label
                      key={a.id}
                      htmlFor={`acc-${a.id}`}
                      className={cn(
                        "flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors",
                        selectedAccountId === a.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      )}
                    >
                      <RadioGroupItem id={`acc-${a.id}`} value={a.id} className="mt-1" />
                      <AccountCard account={a} lenderName={lender} />
                    </label>
                  ))}
                </RadioGroup>
              ) : (
                <div className="space-y-2">
                  {accounts.map((a: LenderBankAccount) => (
                    <div key={a.id} className="rounded-md border p-3">
                      <AccountCard
                        account={a}
                        lenderName={lender}
                        onEdit={() => handleStartEdit(a)}
                        onDelete={() => setAccountToDelete(a)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedLenderId && !loading && accounts.length === 0 && !showAddForm && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Landmark className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No bank account added for {lender}.</p>
              <Button onClick={() => setShowAddForm(true)} size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add Bank Account
              </Button>
            </div>
          )}

          {selectedLenderId && !loading && !showAddForm && accounts.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> Add another account
            </Button>
          )}

          {/* Add / Edit Form */}
          {selectedLenderId && !loading && showAddForm && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  {editingAccountId ? "Edit Account" : "Add New Account"}
                </Label>

                <div className="space-y-2">
                  <RequiredLabel label="Account Type" />
                  <RadioGroup
                    value={form.accountType}
                    onValueChange={(v) =>
                      setForm({ ...form, accountType: v as "current" | "virtual" })
                    }
                    className="grid grid-cols-2 gap-2"
                  >
                    <label
                      htmlFor="at-current"
                      className={cn(
                        "flex items-center gap-2 rounded-md border p-2.5 cursor-pointer text-sm",
                        form.accountType === "current"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      )}
                    >
                      <RadioGroupItem id="at-current" value="current" />
                      Current Account
                    </label>
                    <label
                      htmlFor="at-virtual"
                      className={cn(
                        "flex items-center gap-2 rounded-md border p-2.5 cursor-pointer text-sm",
                        form.accountType === "virtual"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      )}
                    >
                      <RadioGroupItem id="at-virtual" value="virtual" />
                      Virtual Account
                    </label>
                  </RadioGroup>
                </div>

                <Field
                  label="Account Holder Name"
                  value={form.accountHolderName}
                  disabled={verified || validating}
                  onChange={(v) => {
                    setForm({ ...form, accountHolderName: v });
                    clearVerification();
                  }}
                  error={errors.accountHolderName}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Bank Name"
                    value={form.bankName}
                    disabled={verified || validating}
                    onChange={(v) => {
                      setForm({ ...form, bankName: v });
                      clearVerification();
                    }}
                    error={errors.bankName}
                  />
                  <Field
                    label="Branch Name"
                    value={form.branchName}
                    disabled={verified || validating}
                    onChange={(v) => {
                      setForm({ ...form, branchName: v });
                      clearVerification();
                    }}
                    error={errors.branchName}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    label="Account Number"
                    value={form.accountNumber}
                    disabled={verified || validating}
                    inputMode="numeric"
                    maxLength={18}
                    onChange={(v) => {
                      setForm({ ...form, accountNumber: v.replace(/\D/g, "") });
                      clearVerification();
                    }}
                    error={errors.accountNumber}
                  />
                  <div className="space-y-1">
                    <RequiredLabel label="Re-Enter Account Number" />
                    <div className="relative">
                      <Input
                        value={form.confirmAccountNumber}
                        disabled={verified || validating}
                        inputMode="numeric"
                        maxLength={18}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            confirmAccountNumber: e.target.value.replace(/\D/g, ""),
                          });
                          clearVerification();
                        }}
                        className={cn(
                          "h-9 pr-9",
                          (errors.confirmAccountNumber || accountMismatch) && "border-red-500",
                          verified && "bg-muted"
                        )}
                      />
                      {accountsMatch && (
                        <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />
                      )}
                    </div>
                    <p
                      className={cn(
                        "text-[11px]",
                        accountMismatch || errors.confirmAccountNumber
                          ? "text-red-500"
                          : "text-muted-foreground"
                      )}
                    >
                      {accountMismatch || errors.confirmAccountNumber
                        ? errors.confirmAccountNumber || "Account numbers do not match"
                        : "Must match the account number"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <Field
                      label="IFSC Code"
                      value={form.ifscCode}
                      disabled={verified || validating}
                      maxLength={11}
                      onChange={(v) => {
                        setForm({
                          ...form,
                          ifscCode: v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11),
                        });
                        clearVerification();
                      }}
                      error={errors.ifscCode}
                      placeholder="HDFC0001234"
                    />
                  </div>
                  {verified ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0"
                      onClick={clearVerification}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      Edit details
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      className="h-9 shrink-0"
                      disabled={!canValidate}
                      onClick={() => void handleValidate()}
                    >
                      {validating ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <ShieldCheck className="h-4 w-4 mr-1" />
                      )}
                      {validating || saving
                        ? "Validating & saving…"
                        : "Validate & save"}
                    </Button>
                  )}
                </div>

                {verified && (
                  <div className="overflow-hidden rounded-lg border border-emerald-200 bg-emerald-50">
                    <div className="flex items-start justify-between gap-3 px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white">
                          <Check className="h-3 w-3 stroke-[2.5]" />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-emerald-800">Status: Success</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Penny-drop verification completed
                            {verifiedAt ? ` · ${verifiedAt}` : ""}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[10px] text-emerald-700">
                        Verified
                      </Badge>
                    </div>
                    <div className="space-y-0 border-t border-emerald-200 bg-white/70 px-3 py-1 text-xs">
                      {[
                        ["Account status", verifyData?.account_status || "Valid"],
                        ["Name at bank", verifyData?.name_at_bank || "—"],
                        ["Bank name", verifyData?.bank_name || form.bankName || "—"],
                        ["Branch name", verifyData?.branch_name || form.branchName || "—"],
                        ["UTR", verifyData?.utr || "—"],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-b-0"
                        >
                          <span className="text-muted-foreground">{label}</span>
                          <span className="font-medium text-right break-all">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  {accounts.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelForm}
                      className="flex-1"
                      disabled={saving || validating}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={() => void handleSave()}
                    size="sm"
                    className="flex-1"
                    disabled={saving || validating}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                    )}
                    {editingAccountId
                      ? verified
                        ? "Update (Verified)"
                        : "Update Account"
                      : verified
                        ? "Save (Verified)"
                        : "Save Account"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <AlertDialog open={!!accountToDelete} onOpenChange={(open) => !open && setAccountToDelete(null)}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete bank account?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove the bank account
                {accountToDelete ? ` (${accountToDelete.bankName} • ${maskAccount(accountToDelete.accountNumber)})` : ""}.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <Button
                variant="destructive"
                className="bg-red-500 text-white hover:bg-red-600 hover:text-white"
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

function AccountCard({
  account,
  lenderName,
  onEdit,
  onDelete,
}: {
  account: LenderBankAccount;
  lenderName?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [revealed, setRevealed] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(formatBankForCopy(account, lenderName));
      toast.success("Bank details copied" + " " + `${account.bankName} • ${maskAccount(account.accountNumber)}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const toggleReveal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setRevealed((v) => !v);
  };

  return (
    <div className="flex-1 min-w-0">
      {/* Header strip */}
      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={cn(
              "h-7 w-7 rounded-md flex items-center justify-center shrink-0",
              account.isVerified ? "bg-emerald-100" : "bg-primary/10"
            )}
          >
            {account.isVerified ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <Building2 className="h-3.5 w-3.5 text-primary" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="text-sm font-semibold truncate leading-tight">
                {account.bankName}
              </div>
              {account.isVerified && (
                <span
                  title="Bank account verified"
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"
                >
                  <Check className="h-2.5 w-2.5 stroke-[3]" />
                </span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground truncate leading-tight">{account.branchName}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge variant="outline" className="text-[10px] capitalize h-5">
            {account.accountType === "current" ? "Current" : "Virtual"}
          </Badge>
          {account.isVerified ? (
            <Badge
              variant="secondary"
              className="text-[10px] gap-1 h-5 border border-emerald-200 bg-emerald-50 text-emerald-700"
            >
              <ShieldCheck className="h-2.5 w-2.5" />
              Verified
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px] gap-1 h-5">
              <Lock className="h-2.5 w-2.5" />
              Unverified
            </Badge>
          )}
          {/* {onEdit && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit();
              }}
              title="Edit bank account"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {onDelete && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-red-500 hover:text-red-600"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              title="Delete bank account"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )} */}
        </div>
      </div>

      {/* Body rows */}
      <div className="space-y-1.5 text-xs">
        <Row icon={<UserIcon className="h-3 w-3" />} label="Account Holder" value={account.accountHolderName} />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
            <Hash className="h-3 w-3 shrink-0" />
            <span className="shrink-0">A/C No.</span>
            <span className="font-mono text-foreground truncate">
              {revealed ? account.accountNumber : maskAccount(account.accountNumber)}
            </span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={toggleReveal}
              title={revealed ? "Hide" : "Reveal account number"}
            >
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={handleCopy}
              title="Copy bank details"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <Row icon={<Landmark className="h-3 w-3" />} label="IFSC" value={account.ifscCode} mono />
      </div>
    </div>
  );
}

function Row({
  icon,
  label,
  value,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
        {icon}
        <span>{label}</span>
      </div>
      <span className={cn("text-foreground truncate text-right", mono && "font-mono")}>{value}</span>
    </div>
  );
}

function RequiredLabel({ label }: { label: string }) {
  return (
    <Label className="text-xs">
      {label} <span className="text-red-500">*</span>
    </Label>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  placeholder,
  disabled,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
}) {
  return (
    <div className="space-y-1">
      <RequiredLabel label={label} />
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={inputMode}
          maxLength={maxLength}
          className={cn(
            "h-9",
            error && "border-red-500",
            disabled && "bg-muted pr-9"
          )}
        />
        {disabled && (
          <Lock className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>
      {error && (
        <p className="text-[11px] text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> {error}
        </p>
      )}
    </div>
  );
}
